import React, { useState, useEffect, useRef, useMemo } from 'react';
import { StepFormsProps, FormProp } from './types';
import Autocomplete from './autocomplete';
import { ExtendedWindowType } from '../../engine';
import { DataLayerObject } from '../../../helpers/global-types';

const StepForms: React.FC<StepFormsProps> = ({
  inputs,
  errorMessage,
  addressErrorMessage,
  formData,
  setFormData,
  setIsFormValid,
  isModal,
  contractorData,
  twoColumns
}) => {
  const [firstLoad, setFirstLoad] = useState(true);
  const [scrollable, setScrollable] = useState(false);
  const content = useRef(null);
  const autocompleteInput = useRef(null);
  const [formValidation, setFormValidation] = useState<{
    [key: string]: boolean | string;
  }>({});
  const [fieldTouched, setFieldTouched] = useState<{ [key: string]: boolean }>(
    {}
  );

  const [interact, setInteract] = useState(false);
  const requiredValues = useMemo(() => {
    const required = {};
    const numRequired = inputs.reduce((num: number, object: FormProp) => {
      if (object.required === 'true') {
        Object.assign(required, { [object.name]: 'required' });
        num++;
      }
      return num;
    }, 0);

    setFormValidation(required);

    return numRequired;
  }, []);

  useEffect(() => {
    const formValidationArray = Object.entries(formValidation);

    if (!requiredValues) {
      return;
    }
    if (firstLoad) {
      setFirstLoad(false);
      handleValidation('');
      if (formData.address !== '')
        setFormValidation(prevFormValidation => ({
          ...prevFormValidation,
          address: true
        }));
    } else {
      if (
        formValidationArray.findIndex(
          object => object[1] === false || object[1] === 'required'
        ) === -1
      ) {
        setIsFormValid(true);
      } else {
        setIsFormValid(false);
      }
    }
  }, [formValidation]);

  const handleAutocomplete = (value: string, name: string, type: string) => {
    if (type === 'autocomplete') {
      setFormValidation(prevFormValidation => ({
        ...prevFormValidation,
        [name]: value ? true : false
      }));
    }
  };

  const handleAddressBlur = () => {
    setInteract(true);
    setFieldTouched(prevFieldTouched => ({
      ...prevFieldTouched,
      address: true
    }));
  };

  const handleValidation = (name: string) => {
    if (interact) {
      const global = window as ExtendedWindowType;
      if (
        global.dataLayer.findIndex((layer: DataLayerObject) => {
          layer.event === 'request_quote_start';
        }) === -1
      ) {
        global.dataLayer?.push({
          event: 'request_quote_start',
          event_category: 'find a contractor',
          event_action: 'form start',
          ...(contractorData && {
            event_attributes: {
              contractor_id: contractorData?.id,
              contractor_name: contractorData?.contractorName,
              contractor_certificates_count:
                contractorData?.numberOfCertificates,
              contractor_rating: contractorData?.rating,
              contractor_reviews_count: contractorData?.numberOfReviews,
              contractor_type: contractorData?.type,
              form_name: 'request a quote form',
              form_type: 'contractor locator'
            }
          })
        });
      }
    }
    if (!content.current) {
      return;
    }

    let _fieldTouched: { [key: string]: boolean } = {};

    if (name !== '') {
      _fieldTouched[name] = true;
    }
    let _formValidation = formValidation;
    (content.current as HTMLElement)
      ?.querySelectorAll('.field__control')
      .forEach(input => {
        const type = (input as HTMLInputElement).type;
        const name = (input as HTMLInputElement).name;
        const value =
          type === 'checkbox'
            ? (input as HTMLInputElement).checked
            : (input as HTMLInputElement).value;
        const textarea = (input as HTMLInputElement).tagName === 'TEXTAREA';

        if (value && !fieldTouched[name]) {
          _fieldTouched = { ..._fieldTouched, [name]: true };
        }

        if (name === 'address') {
          return;
        }
        if (textarea) {
          _formValidation = {
            ..._formValidation,
            [name]: true
          };
        } else {
          if (!value) {
            _formValidation = {
              ..._formValidation,
              [name]: false
            };
            return;
          } else {
            if (type !== 'tel' && type !== 'email') {
              _formValidation = {
                ..._formValidation,
                [name]: true
              };
            } else {
              if (type === 'tel') {
                _formValidation = {
                  ..._formValidation,
                  [name]: /^\(\d{3}\) \d{3}-\d{4}/g.test(value as string)
                };
              }
              if (type === 'email') {
                _formValidation = {
                  ..._formValidation,
                  [name]: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(
                    value as string
                  )
                };
              }
            }
          }
        }
      });

    setFieldTouched(prevFieldTouched => ({
      ...prevFieldTouched,
      ..._fieldTouched
    }));

    setFormValidation(prevFormValidation => ({
      ...prevFormValidation,
      ..._formValidation
    }));
  };

  const checkValidation = (
    value: boolean | string | undefined,
    name: string
  ) => {
    if (value !== undefined && value !== 'required' && fieldTouched[name]) {
      return value ? '' : 'field--error';
    }
    if (fieldTouched[name] && value === 'required' && name === 'address') {
      return 'field--error';
    }
    return '';
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.trim().replace(/\D/g, '');
    const length = cleaned.length;
    const first = cleaned.substring(0, 3);
    const second = cleaned.substring(3, 6);
    const third = cleaned.substring(6, 10);

    if (length < 4) return `${first}`;
    if (length < 7 && length >= 4) {
      return `(${first}) ${second}`;
    }
    return `(${first}) ${second}-${third}`;
  };

  if (content.current) {
    const contentElement = content.current as HTMLElement;
    if (contentElement.scrollHeight > contentElement.clientHeight) {
      if (!scrollable) {
        setScrollable(true);
      }
    } else {
      if (scrollable) {
        setScrollable(false);
      }
    }
  }

  const renderOneColumnLayout = (inputs: FormProp[]) => {
    return (
      <>
        {inputs?.map(input => (
          <>
            {input.name !== 'terms' && (
              <div
                key={input.label.trim().replace(' ', '_').toLowerCase()}
                className={`field ${
                  input.required === 'true' && 'field--required'
                } ${checkValidation(formValidation[input.name], input.name)}`}
              >
                <label
                  className="field__label"
                  htmlFor={input.label.trim().replace(' ', '_').toLowerCase()}
                >
                  {input.label}
                </label>
                {input.type !== 'textarea' ? (
                  <>
                    {input.google ? (
                      <Autocomplete
                        label={input.label}
                        name={input.name}
                        placeholder={input.placeholder}
                        required={input.required}
                        type={input.type}
                        setFormData={setFormData}
                        handleValidation={handleAutocomplete}
                        formDataValue={
                          formData[input.name] as string | undefined
                        }
                        onBlur={handleAddressBlur}
                      />
                    ) : (
                      <input
                        className="field__input field__control"
                        type={input.type}
                        ref={
                          input.type === 'address' ? autocompleteInput : null
                        }
                        id={input.label.trim().replace(' ', '_').toLowerCase()}
                        name={input.name}
                        placeholder={input.placeholder}
                        required={input.required === 'true' ? true : false}
                        value={
                          formData[input.name]
                            ? (formData[input.name] as string)
                            : ''
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setInteract(true);
                          if (input.type === 'tel') {
                            setFormData(prevFormData => ({
                              ...prevFormData,
                              [input.name]: formatPhoneNumber(e.target.value)
                            }));
                          } else {
                            setFormData(prevFormData => ({
                              ...prevFormData,
                              [input.name]: e.target.value
                            }));
                          }
                        }}
                        onBlur={e => handleValidation(e.target.name)}
                      />
                    )}
                  </>
                ) : (
                  <textarea
                    rows={3}
                    className={`field__input field__control message-field ${checkValidation(
                      formValidation[input.name],
                      input.name
                    )}`}
                    id={input.label.trim().replace(' ', '_').toLowerCase()}
                    name={input.name}
                    placeholder={input.placeholder}
                    required={input.required === 'true' ? true : false}
                    value={
                      formData[input.name]
                        ? (formData[input.name] as string)
                        : ''
                    }
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      setInteract(true);
                      setFormData(prevFormData => ({
                        ...prevFormData,
                        [input.name]: e.target.value.substring(0, 250)
                      }));
                    }}
                    onBlur={e => handleValidation(e.target.name)}
                  ></textarea>
                )}
                {input.google ? (
                  <span className="field__error-msg">
                    {addressErrorMessage}
                  </span>
                ) : (
                  <span className="field__error-msg">{errorMessage}</span>
                )}
              </div>
            )}
          </>
        ))}
      </>
    );
  };

  const renderTwoColumnLayout = (inputs: FormProp[]) => {
    const twoColumnsInputs = [] as FormProp[];
    const remainingInputs = [] as FormProp[];

    inputs.forEach(input => {
      if (
        input.name !== 'terms' &&
        !input.google &&
        input.type !== 'textarea'
      ) {
        twoColumnsInputs.push(input);
      } else {
        remainingInputs.push(input);
      }
    });

    return (
      <>
        <div className="request-a-quote__grid">
          {twoColumnsInputs.map(input => (
            <div
              key={input.label.trim().replace(' ', '_').toLowerCase()}
              className={`field ${
                input.required === 'true' && 'field--required'
              } ${checkValidation(formValidation[input.name], input.name)}`}
            >
              <label
                className="field__label"
                htmlFor={input.label.trim().replace(' ', '_').toLowerCase()}
              >
                {input.label}
              </label>
              <input
                className="field__input field__control"
                type={input.type}
                ref={input.type === 'address' ? autocompleteInput : null}
                id={input.label.trim().replace(' ', '_').toLowerCase()}
                name={input.name}
                placeholder={input.placeholder}
                required={input.required === 'true' ? true : false}
                value={
                  formData[input.name] ? (formData[input.name] as string) : ''
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setInteract(true);
                  if (input.type === 'tel') {
                    setFormData(prevFormData => ({
                      ...prevFormData,
                      [input.name]: formatPhoneNumber(e.target.value)
                    }));
                  } else {
                    setFormData(prevFormData => ({
                      ...prevFormData,
                      [input.name]: e.target.value
                    }));
                  }
                }}
                onBlur={e => handleValidation(e.target.name)}
              />
              <span className="field__error-msg">{errorMessage}</span>
            </div>
          ))}
        </div>
        {renderOneColumnLayout(remainingInputs)}
      </>
    );
  };

  return (
    <div
      ref={content}
      className={`request-a-quote__content request-a-quote__content--form ${
        scrollable ? 'request-a-quote__content--scrollable' : ''
      } ${isModal ? 'request-a-quote__content--modal' : ''}`}
    >
      {twoColumns
        ? renderTwoColumnLayout(inputs)
        : renderOneColumnLayout(inputs)}
    </div>
  );
};

export default StepForms;
